import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDateRange } from '../lib/dateUtils';
import './ExportPage.css';

function groupByYear(records) {
  const map = new Map();
  for (const record of records) {
    const year = record.start_date?.slice(0, 4) ?? '?';
    if (!map.has(year)) map.set(year, []);
    map.get(year).push(record);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([year, recs]) => ({
      year,
      records: recs.sort((a, b) =>
        (b.start_date ?? '').localeCompare(a.start_date ?? '')
      ),
    }));
}

export default function ExportPage({ user, currentProfile, onBack }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYears, setSelectedYears] = useState(new Set());
  const [allYears, setAllYears] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    if (!currentProfile?.id) return;

    const fetchRecords = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('records')
        .select('*, record_shares!inner(profile_id)')
        .eq('user_id', user.id)
        .eq('record_shares.profile_id', currentProfile.id)
        .order('start_date', { ascending: true });

      const recs = data || [];
      setRecords(recs);

      const years = [...new Set(recs.map((r) => r.start_date?.slice(0, 4)).filter(Boolean))].sort().reverse();
      setAllYears(years);
      setSelectedYears(new Set(years));

      const cats = [...new Set(recs.map((r) => r.category_name).filter(Boolean))].sort();
      setAllCategories(cats);
      setSelectedCategories(new Set(cats));
      setLoading(false);
    };

    fetchRecords();
  }, [user.id, currentProfile?.id]);

  const toggleYear = (year) => {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const toggleAllYears = () => {
    if (selectedYears.size === allYears.length) {
      setSelectedYears(new Set());
    } else {
      setSelectedYears(new Set(allYears));
    }
  };

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const toggleAllCategories = () => {
    if (selectedCategories.size === allCategories.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(allCategories));
    }
  };

  const filtered = records.filter((r) =>
    selectedYears.has(r.start_date?.slice(0, 4)) &&
    selectedCategories.has(r.category_name)
  );
  const yearGroups = groupByYear(filtered);
  const totalCount = filtered.length;
  const exportDate = new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="export-page">
      {/* 컨트롤 영역 (인쇄 시 숨김) */}
      <div className="export-controls no-print">
        <header className="export-header">
          <button className="export-back-btn" onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="export-title">PDF인쇄 / 앨범만들기</h1>
          <button
            className="export-print-btn"
            onClick={handlePrint}
            disabled={loading || totalCount === 0}
          >
            인쇄
          </button>
        </header>

        {loading ? (
          <div className="export-loading">기록을 불러오는 중...</div>
        ) : (
          <>
            <div className="export-year-section">
              <p className="export-section-label">연도 선택</p>
              <div className="export-year-chips">
                <button
                  className={`export-year-chip${selectedYears.size === allYears.length ? ' export-year-chip--active' : ''}`}
                  onClick={toggleAllYears}
                >
                  전체
                </button>
                {allYears.map((year) => (
                  <button
                    key={year}
                    className={`export-year-chip${selectedYears.has(year) ? ' export-year-chip--active' : ''}`}
                    onClick={() => toggleYear(year)}
                  >
                    {year}년
                  </button>
                ))}
              </div>
            </div>

            <div className="export-year-section">
              <p className="export-section-label">카테고리 선택</p>
              <div className="export-year-chips">
                <button
                  className={`export-year-chip${selectedCategories.size === allCategories.length ? ' export-year-chip--active' : ''}`}
                  onClick={toggleAllCategories}
                >
                  전체
                </button>
                {allCategories.map((cat) => (
                  <button
                    key={cat}
                    className={`export-year-chip${selectedCategories.has(cat) ? ' export-year-chip--active' : ''}`}
                    onClick={() => toggleCategory(cat)}
                  >
                    {records.find((r) => r.category_name === cat)?.category_emoji ?? ''} {cat}
                  </button>
                ))}
              </div>
              <p className="export-count">{totalCount}개의 기록이 선택됨</p>
            </div>
          </>
        )}
      </div>

      {/* 인쇄 영역 */}
      {!loading && totalCount > 0 && (
        <div className="export-print-area">
          <div className="export-print-header">
            <h2 className="export-print-title">
              인생로그 — {currentProfile?.emoji} {currentProfile?.name}
            </h2>
            <p className="export-print-date">내보내기 날짜: {exportDate}</p>
          </div>

          {yearGroups.map(({ year, records: yearRecords }) => (
            <div key={year} className="export-year-group">
              <div className="export-year-header">
                <span className="export-year-label">{year}년</span>
                <span className="export-year-count">{yearRecords.length}건</span>
              </div>
              {yearRecords.map((record) => (
                <div key={record.id} className="export-record">
                  <div className="export-record-header">
                    <span className="export-record-emoji">{record.category_emoji ?? '📌'}</span>
                    <span className="export-record-title">{record.title}</span>
                  </div>
                  <div className="export-record-meta">
                    {record.category_name && (
                      <span className="export-record-category">{record.category_name}</span>
                    )}
                    <span className="export-record-date">
                      {formatDateRange(record.start_date, record.end_date, record.is_ongoing)}
                    </span>
                  </div>
                  {record.location_name && (
                    <p className="export-record-location">📍 {record.location_name}</p>
                  )}
                  {record.memo && (
                    <p className="export-record-memo">{record.memo}</p>
                  )}
                  {record.photos?.length > 0 && (
                    <div className="export-record-photos">
                      {record.photos.map((url, i) => (
                        <img key={i} src={url} alt="" className="export-record-photo" />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {!loading && totalCount === 0 && (
        <div className="export-empty no-print">
          <p>선택된 기록이 없습니다.</p>
          <p>연도를 선택해주세요.</p>
        </div>
      )}
    </div>
  );
}
